import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { LedgerField, LedgerInput } from '../ui';

const validationSchema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password should be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number',
    ),
  passwordConfirm: yup
    .string()
    .required('Password confirmation is required')
    // yup's .oneOf() is an internal "presence" test that only skips null/undefined,
    // never '' — so it would fail an empty confirm against a filled password with
    // "Passwords must match" before .required()'s own empty check ever runs. A plain
    // .test() that no-ops on empty values lets .required() own the empty case.
    .test('passwords-match', 'Passwords must match', function (value) {
      if (!value) return true;
      return value === this.parent.password;
    }),
});

type ResetValues = yup.InferType<typeof validationSchema>;

export const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { confirmPasswordReset, isLoading, error } = useAuth();
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetValues>({ resolver: yupResolver(validationSchema), mode: 'onTouched' });

  const onSubmit = async (values: ResetValues) => {
    if (!token) return;
    try {
      await confirmPasswordReset({ token, ...values });
      setIsSuccess(true);
    } catch {
      // The auth slice owns the error state.
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (isSuccess) {
    return (
      <div>
        <h2 className="mb-2 font-serif text-scale-3 text-ink">Password reset</h2>
        <p className="text-scale-1 text-ink-muted">Your password has been reset successfully.</p>
        <p className="mt-4 text-center text-scale-1">
          <RouterLink to="/login" className="text-accent hover:underline">
            ← Back to sign in
          </RouterLink>
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-2 font-serif text-scale-3 text-ink">Reset password</h2>
      <p className="mb-3 text-scale-1 text-ink-muted">Enter your new password below.</p>
      {error && (
        <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
          {error}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LedgerField label="New password" htmlFor="password" error={errors.password?.message}>
          <LedgerInput id="password" revealToggle autoComplete="new-password" autoFocus invalid={!!errors.password} {...register('password')} />
        </LedgerField>
        <LedgerField label="Confirm password" htmlFor="passwordConfirm" error={errors.passwordConfirm?.message}>
          <LedgerInput id="passwordConfirm" revealToggle autoComplete="new-password" invalid={!!errors.passwordConfirm} {...register('passwordConfirm')} />
        </LedgerField>
        <button
          type="submit"
          disabled={isLoading}
          className="mt-1 w-full cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:opacity-60"
        >
          {isLoading ? 'Resetting…' : 'Reset password'}
        </button>
      </form>
    </div>
  );
};
